import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMe } from '@/hooks/useUser';
import {
  useComplaints,
  usePendingAuctions,
  useBans,
  useResolveComplaint,
  useModerateAuction,
  useUnban,
  useBanByUsername,
} from '@/hooks/useAdmin';
import { formatMoney } from '@/lib/format';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { PhotoLightbox } from '@/components/common/PhotoLightbox';
import { haptic } from '@/lib/telegram';
import styles from './AdminPage.module.css';

type Tab = 'moderation' | 'complaints' | 'bans';

export const AdminPage = () => {
  const { data: me, isLoading: meLoading } = useMe();
  const [tab, setTab] = useState<Tab>('moderation');

  if (meLoading) {
    return <div className={styles.page}><Skeleton width="100%" height={60} /></div>;
  }

  if (!me || me.role === 'user') {
    return <Navigate to="/" replace />;
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Модерация</h1>

      <div className={styles.tabs}>
        <TabBtn active={tab === 'moderation'} onClick={() => setTab('moderation')}>Лоты</TabBtn>
        <TabBtn active={tab === 'complaints'} onClick={() => setTab('complaints')}>Споры</TabBtn>
        <TabBtn active={tab === 'bans'} onClick={() => setTab('bans')}>Баны</TabBtn>
      </div>

      {tab === 'moderation' && <ModerationTab />}
      {tab === 'complaints' && <ComplaintsTab />}
      {tab === 'bans' && <BansTab />}
    </div>
  );
};

const TabBtn = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
  <button className={active ? styles.tabActive : styles.tab} onClick={() => { haptic.light(); onClick(); }}>
    {children}
  </button>
);

// ── Вкладка: модерация лотов ──
const ModerationTab = () => {
  const { data, isLoading } = usePendingAuctions();
  const moderate = useModerateAuction();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [lightbox, setLightbox] = useState<{ urls: string[]; index: number } | null>(null);

  if (isLoading) return <Skeleton width="100%" height={120} />;
  if (!data || data.length === 0) {
    return <EmptyState title="Очередь пуста" description="Нет лотов на модерации" />;
  }

  return (
    <>
      <div className={styles.list}>
        {data.map((a) => (
          <div key={a.id} className={styles.modCard}>
            {/* Галерея всех фото — горизонтальный скролл */}
            {a.photos && a.photos.length > 0 && (
              <div className={styles.gallery}>
                {a.photos.map((p, i) => (
                  <button
                    key={i}
                    type="button"
                    className={styles.galleryBtn}
                    onClick={() => {
                      haptic.light();
                      setLightbox({
                        urls: a.photos.map((photo) => photo.url_original),
                        index: i,
                      });
                    }}
                    aria-label={`Открыть фото ${i + 1}`}
                  >
                    <img src={p.url_medium} alt="" className={styles.galleryImage} />
                  </button>
                ))}
              </div>
            )}

          <div className={styles.cardBody}>
            <p className={styles.cardTitle}>{a.title}</p>
            <p className={styles.cardMeta}>
              {formatMoney(a.start_price)} · {a.duration_minutes} мин · @{a.seller.telegram_username ?? a.seller.display_name}
            </p>
            <p className={styles.cardDesc}>{a.description}</p>
            <div className={styles.cardTags}>
              {a.tags.map((t) => <span key={t.slug} className={styles.tag}>{t.name_ru}</span>)}
            </div>

            {rejectingId === a.id ? (
              <div className={styles.rejectBox}>
                <textarea
                  className={styles.rejectInput}
                  placeholder="Причина отклонения (увидит продавец)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                />
                <div className={styles.actions}>
                  <Button variant="secondary" onClick={() => { setRejectingId(null); setReason(''); }}>Отмена</Button>
                  <Button
                    variant="danger"
                    loading={moderate.isPending}
                    disabled={!reason.trim()}
                    onClick={() => {
                      haptic.warning?.() ?? haptic.medium();
                      moderate.mutate(
                        { id: a.id, action: 'reject', note: reason.trim() },
                        { onSuccess: () => { setRejectingId(null); setReason(''); } },
                      );
                    }}
                  >
                    Отклонить
                  </Button>
                </div>
              </div>
            ) : (
              <div className={styles.actions}>
                <Button
                  variant="primary"
                  loading={moderate.isPending}
                  onClick={() => { haptic.medium(); moderate.mutate({ id: a.id, action: 'approve', note: null }); }}
                >
                  Одобрить
                </Button>
                <Button variant="secondary" onClick={() => { haptic.light(); setRejectingId(a.id); }}>
                  Отклонить
                </Button>
              </div>
            )}
          </div>
        </div>
      ))}
      </div>

      <PhotoLightbox
        urls={lightbox?.urls ?? []}
        initialIndex={lightbox?.index ?? 0}
        isOpen={lightbox !== null}
        onClose={() => setLightbox(null)}
      />
    </>
  );
};

const formatAdminUser = (user: { display_name: string; telegram_username: string | null }) =>
  user.telegram_username ? `@${user.telegram_username}` : user.display_name;

// ── Вкладка: споры ──
const ComplaintsTab = () => {
  const { data, isLoading } = useComplaints();
  const resolve = useResolveComplaint();

  if (isLoading) return <Skeleton width="100%" height={120} />;
  if (!data || data.length === 0) {
    return <EmptyState title="Споров нет" description="Открытых жалоб не найдено" />;
  }

  const categoryLabel = (c: string) =>
    c === 'seller_refused_sale' ? 'Продавец сорвал сделку' : 'Покупатель сорвал сделку';

  return (
    <div className={styles.list}>
      {data.map((group) => (
        <div key={group.auction?.id ?? group.complaints[0]?.id} className={styles.card}>
          <div className={styles.cardBody}>
            <p className={styles.cardTitle}>{group.auction?.title ?? 'Лот удалён'}</p>

            {group.complaints.map((c) => (
              <div key={c.id} className={styles.complaintBlock}>
                <p className={styles.complaintCat}>{categoryLabel(c.category)}</p>
                <p className={styles.cardMeta}>
                  Жалоба от: {formatAdminUser(c.reporter)}
                </p>
                {c.accused && (
                  <p className={styles.cardMeta}>
                    На кого: {formatAdminUser(c.accused)}
                  </p>
                )}
                {c.description && <p className={styles.cardDesc}>«{c.description}»</p>}

                <div className={styles.actions}>
                  <Button
                    variant="danger"
                    loading={resolve.isPending}
                    onClick={() => {
                      haptic.warning?.() ?? haptic.medium();
                      resolve.mutate({ id: c.id, decision: 'ban', note: null });
                    }}
                  >
                    Забанить виновного
                  </Button>
                  <Button
                    variant="secondary"
                    loading={resolve.isPending}
                    onClick={() => {
                      haptic.light();
                      resolve.mutate({ id: c.id, decision: 'dismiss', note: null });
                    }}
                  >
                    Отклонить жалобу
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Вкладка: баны ──
const BansTab = () => {
  const { data, isLoading } = useBans();
  const unban = useUnban();
  const banByUsername = useBanByUsername();
  const [username, setUsername] = useState('');
  const [banNote, setBanNote] = useState('');
  const [banError, setBanError] = useState<string | null>(null);

  const reasonLabel = (r: string) => {
    const map: Record<string, string> = {
      seller_refused_sale: 'Сорвал продажу',
      buyer_refused_purchase: 'Сорвал покупку',
      manual_admin: 'Вручную',
      fraud_detected: 'Мошенничество',
      spam: 'Спам',
      inappropriate_content: 'Недопустимый контент',
    };
    return map[r] ?? r;
  };

  const submitBan = () => {
    setBanError(null);
    const trimmed = username.trim();
    if (!trimmed) {
      setBanError('Введи @username в Telegram');
      return;
    }
    haptic.medium();
    banByUsername.mutate(
      { telegramUsername: trimmed, details: banNote.trim() || null },
      {
        onSuccess: () => {
          setUsername('');
          setBanNote('');
        },
        onError: (err) => {
          setBanError(err instanceof Error ? err.message : 'Не удалось забанить');
        },
      },
    );
  };

  return (
    <div className={styles.list}>
      <div className={styles.banForm}>
        <p className={styles.banFormTitle}>Заблокировать по Telegram</p>
        <input
          className={styles.rejectInput}
          placeholder="@username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoCapitalize="none"
          autoCorrect="off"
        />
        <textarea
          className={styles.rejectInput}
          placeholder="Комментарий (необязательно)"
          rows={2}
          value={banNote}
          onChange={(e) => setBanNote(e.target.value)}
        />
        {banError && <p className={styles.banFormError}>{banError}</p>}
        <Button
          variant="primary"
          loading={banByUsername.isPending}
          onClick={submitBan}
        >
          Забанить
        </Button>
      </div>

      {isLoading ? (
        <Skeleton width="100%" height={80} />
      ) : !data || data.length === 0 ? (
        <EmptyState title="Банов нет" description="Заблокированных пользователей нет" />
      ) : (
        data.map((b) => (
          <div key={b.id} className={styles.banRow}>
            <div>
              <p className={styles.cardTitle}>{b.user.display_name}</p>
              <p className={styles.cardMeta}>
                {reasonLabel(b.reason)}
                {b.user.telegram_username ? ` · @${b.user.telegram_username}` : ''}
              </p>
              {b.details && <p className={styles.cardDesc}>{b.details}</p>}
            </div>
            <Button
              variant="secondary"
              loading={unban.isPending}
              onClick={() => {
                haptic.medium();
                unban.mutate({ userId: b.user.id, note: null });
              }}
            >
              Разбанить
            </Button>
          </div>
        ))
      )}
    </div>
  );
};